import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function AgentiMonitor() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [agenti, setAgenti] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("provvigioni"); // provvigioni | fatturato | ordini | nome

  const euro = (n) => `€ ${Number(n || 0).toFixed(2)}`;

  // ---------- helper date ----------
  const toDate = (val) => {
    if (!val) return null;
    let s = String(val);
    if (s.includes(" ") && !s.includes("T")) s = s.replace(" ", "T");
    const d = new Date(s);
    return isNaN(d) ? null : d;
  };
  const pad2 = (n) => String(n).padStart(2, "0");
  const formatLastOrder = (val) => {
    const d = toDate(val);
    if (!d) return "-";
    const day = pad2(d.getDate());
    const month = pad2(d.getMonth() + 1);
    const year = d.getFullYear();
    const hh = pad2(d.getHours());
    const mm = pad2(d.getMinutes());
    return `${day}/${month}/${year} ${hh}.${mm}`;
  };
  // ---------------------------------

  
  
  const fetchAgenti = async () => {
    setErr("");
    setLoading(true);
    try {
      const token = localStorage.getItem("access");

      const res = await fetch(`${API_URL}/api/agenti/monitor/?_=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Errore caricamento agenti");

      const mapped = (Array.isArray(data) ? data : []).map((a) => ({
        id: a.id,
        username: a.username,
        name: a.full_name || a.username,
        email: a.email || "",
        store: a.store_name || "",
        percentuale: a.percentuale ?? null,
        acquired_company_percent: a.acquired_company_percent ?? 8,
        assigned_company_percent: a.assigned_company_percent ?? 5,
        kpi: {
          tot_ordini: Number(a.tot_ordini || 0),
          tot_fatturato: Number(a.tot_fatturato || 0),
          tot_commissioni: Number(a.commissioni_maturate || 0),
          commissioni_pendenti: Number(a.commissioni_pendenti || 0),
          bozze: 0,
        },
        last_order_date: a.last_order_date || null,
      }));

      setAgenti(mapped);
    } catch (e) {
      setErr(e.message || "Errore");
      setAgenti([]);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchAgenti();
  }, []); // eslint-disable-line

  // filtro + sort
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let arr = [...agenti];
    if (term) {
      arr = arr.filter((a) =>
        [a.name, a.username, a.email, a.store]
          .map((s) => (s || "").toLowerCase())
          .some((s) => s.includes(term))
      );
    }
    const by = sortBy;
    arr.sort((a, b) => {
      if (by === "provvigioni")
        return b.kpi.tot_commissioni - a.kpi.tot_commissioni;
      if (by === "fatturato")
        return b.kpi.tot_fatturato - a.kpi.tot_fatturato;
      if (by === "ordini") return b.kpi.tot_ordini - a.kpi.tot_ordini;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [agenti, q, sortBy]);

  const totals = useMemo(() => {
    const n = filtered.length;
    const totFatt = filtered.reduce((acc, a) => acc + a.kpi.tot_fatturato, 0);
    const totProv = filtered.reduce((acc, a) => acc + a.kpi.tot_commissioni, 0);
    const totPend = filtered.reduce(
      (acc, a) => acc + a.kpi.commissioni_pendenti,
      0
    );
    const totOrd = filtered.reduce((acc, a) => acc + a.kpi.tot_ordini, 0);
    return {
      n,
      totFatt: totFatt.toFixed(2),
      totProv: totProv.toFixed(2),
      totPend: totPend.toFixed(2),
      totOrd,
    };
  }, [filtered]);

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/" />

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h2 className="m-0">🧑‍💼 Monitor agenti</h2>
        <div className="d-flex gap-2">
          <div className="d-inline-flex align-items-center gap-3 p-2 border rounded">
            <span className="text-muted">Agenti:</span>
            <strong>{totals.n}</strong>
            <span className="text-muted ms-3">Ordini:</span>
            <strong>{totals.totOrd}</strong>
            <span className="text-muted ms-3">Fatturato:</span>
            <strong>{euro(totals.totFatt)}</strong>
            <span className="text-muted ms-3">Provvigioni:</span>
            <strong>{euro(totals.totProv)}</strong>
            <span className="text-muted ms-3">Pendenti:</span>
            <strong>{euro(totals.totPend)}</strong>
          </div>
          <button className="btn btn-outline-primary" onClick={fetchAgenti}>
            🔄 Aggiorna
          </button>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {loading ? (
        <p className="text-muted">Caricamento…</p>
      ) : (
        <>
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-6">
                  <label className="form-label mb-1">Cerca agente</label>
                  <input
                    className="form-control"
                    placeholder="🔎 Nome, username, email o store…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label mb-1">Ordina per</label>
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="provvigioni">Provvigioni maturate</option>
                    <option value="fatturato">Fatturato</option>
                    <option value="ordini"># Ordini</option>
                    <option value="nome">Nome</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="alert alert-info">Nessun agente trovato.</div>
          ) : (
            <div className="row g-3">
              {filtered.map((a) => (
                <div className="col-12 col-md-6 col-xl-4" key={a.id}>
                  <div className="card shadow-sm h-100">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="m-0">{a.name}</h5>
                          <div className="text-muted small">
                            @{a.username} {a.store ? `• ${a.store}` : ""}
                          </div>
                          {a.email && (
                            <div className="text-muted small">{a.email}</div>
                          )}
                        </div>
                        <span className="badge bg-secondary">AGENTE</span>
                      </div>

                      <div className="mt-3 p-2 border rounded bg-light">
                        <div className="small text-muted mb-1">
                          Provvigioni B2B
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Aziende acquisite</span>
                          <strong>{Number(a.acquired_company_percent || 0).toFixed(2)}%</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Aziende assegnate</span>
                          <strong>{Number(a.assigned_company_percent || 0).toFixed(2)}%</strong>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Fatturato</span>
                          <strong>{euro(a.kpi.tot_fatturato)}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Provvigioni maturate</span>
                          <strong>{euro(a.kpi.tot_commissioni)}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Provvigioni pendenti</span>
                          <strong>{euro(a.kpi.commissioni_pendenti)}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted"># Ordini</span>
                          <strong>{a.kpi.tot_ordini}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Ultimo ordine</span>
                          <strong>{formatLastOrder(a.last_order_date)}</strong>
                        </div>
                      </div>

                      <div className="mt-3 d-flex gap-2">
                        <button
                          className="btn btn-outline-dark btn-sm"
                          title="Apri ordini di questo agente"
                          onClick={() =>
                            nav(`/ordini?q=${encodeURIComponent(a.username)}`)
                          }
                        >
                          Ordini
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          title="Dashboard dettagliata"
                          onClick={() =>
                            nav(`/dashboard-agente?user_id=${a.id}`)
                          }
                        >
                          Dettagli
                        </button>
                      </div>

                      <div className="mt-2 small text-muted">
                        Totale dovuto all’agente:{" "}
                        <strong>{euro(a.kpi.tot_commissioni)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
